import React, { useState } from 'react';
import { Drawer, Typography } from '@navi/web-ui/lib/primitives';
import styles from './index.module.scss';
import { CircleIcon, ThreeDotVerticalIcon } from '@navi/web-ui/lib/icons';
import {
  PRIMARY_SOURCE_MAPPING,
  PrimarySourcesType,
  SimilarAddressDrawerProps,
  SimilarLocation
} from './interfaces';
import dayjs from 'dayjs';
import { DateFormat } from '@cp/src/utils/DateHelper';
import { Tooltip, TooltipTrigger, TooltipContent } from '@cp/src/components/TooltipV2/TooltipV2';
import LonghornInfoPopper from './AddressInfoPopover/LonghornInfoPopper';
import RecentActivityBadge from './AddressRecentActivity/RecentActivityBadge';
import RecentActivityChangePopover from './AddressRecentActivity/RecentActivityChangePopover';
import { updateRecentActivity, fetchTopAddresses } from '../../actions/topAddressesActions';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { RootState } from '@cp/src/store';
import { Roles } from '@cp/src/pages/auth/constants/AuthConstants';
import logger from '@cp/src/utils/logger';

const SimilarAddressDrawer: React.FC<SimilarAddressDrawerProps> = props => {
  const { isOpen, onClose, address, onViewProof } = props;
  const { loanId = '', customerId = '', caseReferenceId = '' } = useParams();
  const dispatch = useDispatch();
  const { pinCode, city, similarLocationEntities } = address;
  const [recentActivityPopoverOpenFor, setRecentActivityPopoverOpenFor] = useState<string | null>(
    null
  );
  const user = useSelector((state: RootState) => state.common.userData);
  const hasGlobalAccess = user?.roles?.includes(Roles.ROLE_GLOBAL_ACCESS);
  const [isUpdatingRecentActivity, setIsUpdatingRecentActivity] = useState(false);

  const handleViewProofClick = (source: string, proofOfSourceUrl: string) => {
    onViewProof?.(source, proofOfSourceUrl);
  };

  const handleThreeDotClick = (referenceId: string) => {
    setRecentActivityPopoverOpenFor(
      recentActivityPopoverOpenFor === referenceId ? null : referenceId
    );
  };

  const createThreeDotClickHandler = (referenceId: string) => () =>
    handleThreeDotClick(referenceId);

  const createRecentActivitySaveHandler = (referenceId: string) => (date: Date) =>
    handleRecentActivitySave(referenceId, date);

  const handleRecentActivityCancel = () => {
    setRecentActivityPopoverOpenFor(null);
  };

  const handleRecentActivitySave = async (addressId: string, date: Date) => {
    setIsUpdatingRecentActivity(true);
    try {
      const formattedDate = dayjs(date).format('YYYY-MM-DD');
      await updateRecentActivity(customerId, addressId, formattedDate);
      toast.success('Recent activity updated successfully');
      setRecentActivityPopoverOpenFor(null);
      dispatch(fetchTopAddresses(loanId, customerId, caseReferenceId));
      onClose();
    } catch (error) {
      toast.error('Failed to update recent activity');
      logger({
        msg: 'Failed to update recent activity',
        extras: {
          error: error
        }
      });
    } finally {
      setIsUpdatingRecentActivity(false);
    }
  };

  const headerText = (
    <Typography variant="h3" color="var(--grayscale-content-2)">
      Similar addresses
    </Typography>
  );

  return (
    <Drawer
      onClose={onClose}
      open={isOpen}
      headerText={headerText}
      headerContainerClasses="w-[450px] !pr-3 "
      showHeaderDivider
      contentClasses="overflow-auto"
    >
      <div className={styles.similarAddressContainer}>
        <Typography variant="p3" color="var(--grayscale-content-2)">
          {pinCode}, {city}
        </Typography>
        {similarLocationEntities?.map((similarLocationEntity: SimilarLocation) => (
          <div key={similarLocationEntity?.referenceId} className={styles.similarAddressItem}>
            <CircleIcon color="var(--geolocation-stepper-ellipse-color)" height={9} width={9} />
            <div>
              {similarLocationEntity.metadata?.recentActivityDate && (
                <div style={{ marginBottom: '4px' }}>
                  <RecentActivityBadge metadata={similarLocationEntity.metadata} />
                </div>
              )}
              <Typography variant="p2" color="var(--grayscale-content-2)">
                {[
                  similarLocationEntity?.addressText,
                  similarLocationEntity?.pinCode,
                  similarLocationEntity?.city
                ]
                  .filter(line => !!line)
                  .join(', ')}
              </Typography>

              {(similarLocationEntity?.capturedAt ||
                similarLocationEntity?.source ||
                similarLocationEntity?.secondarySource) && (
                <Typography
                  variant="p4"
                  color="var(--grayscale-content-3)"
                  className={styles.dateContainer}
                >
                  {similarLocationEntity?.capturedAt &&
                    dayjs(similarLocationEntity?.capturedAt).format(
                      DateFormat.LONG_DATE_FORMAT_WITHOUT_TIME
                    )}
                  {similarLocationEntity?.capturedAt && similarLocationEntity?.source && (
                    <CircleIcon color="var(--grayscale-content-3)" height={4.2} width={4.2} />
                  )}

                  {similarLocationEntity?.source === PrimarySourcesType.LONGHORN_CIU &&
                    hasGlobalAccess && (
                      <LonghornInfoPopper
                        addressEntity={similarLocationEntity}
                        onViewProof={handleViewProofClick}
                        iconSize={12}
                      />
                    )}
                  {PRIMARY_SOURCE_MAPPING[
                    similarLocationEntity?.source as keyof typeof PRIMARY_SOURCE_MAPPING
                  ] ?? similarLocationEntity?.source}
                </Typography>
              )}
            </div>

            <Tooltip
              placement="bottom-end"
              open={recentActivityPopoverOpenFor === similarLocationEntity.referenceId}
              onOpenChange={open => {
                if (!open) setRecentActivityPopoverOpenFor(null);
              }}
            >
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={createThreeDotClickHandler(similarLocationEntity.referenceId)}
                  className={styles.threeDotButton}
                >
                  <ThreeDotVerticalIcon color="var(--grayscale-content-2)" />
                </button>
              </TooltipTrigger>
              <TooltipContent bgColor="transparent" style={{ zIndex: 1301 }}>
                <RecentActivityChangePopover
                  initialDate={
                    similarLocationEntity.metadata?.recentActivityDate
                      ? new Date(similarLocationEntity.metadata.recentActivityDate)
                      : null
                  }
                  onCancel={handleRecentActivityCancel}
                  onSave={createRecentActivitySaveHandler(similarLocationEntity.referenceId)}
                  isLoading={isUpdatingRecentActivity}
                />
              </TooltipContent>
            </Tooltip>
          </div>
        ))}
      </div>
    </Drawer>
  );
};

export default SimilarAddressDrawer;
